import { useResourceParams, useWarnAboutChange } from '@refinedev/core';
import { Form, FormInstance } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Aplica initialValues + borrador (localStorage) y persiste el formulario completo
 * en cada cambio (con debounce). Pensado para simplicidad y lectura.
 */
interface UseFormDraftInitOptions<T> {
  form: FormInstance<T> | undefined;
  draftKey?: string;
  draftEnabled?: boolean;
  debounceMs?: number;
  initialValues?: Partial<T>;
  excludedDraftFields?: (keyof T | string)[];
}

// Mantiene estable la dependencia por defecto para evitar recalcular initialValues en cada render.
const DEFAULT_EXCLUDED_DRAFT_FIELDS: string[] = [];
const useFormDraftInit = <T extends object>({
  form,
  draftKey,
  draftEnabled = true,
  debounceMs = 200,
  initialValues,
  excludedDraftFields = DEFAULT_EXCLUDED_DRAFT_FIELDS,
}: UseFormDraftInitOptions<T>) => {
  const [hasInitialSetted, setHasInitialSetted] = useState(false);
  const draftTouchedRef = useRef(false);
  const lastAppliedBaseInitialValuesRef = useRef<Record<string, any> | undefined>(undefined);
  const latestValues = Form.useWatch([], form);
  const { setWarnWhen } = useWarnAboutChange();
  const { resource, action } = useResourceParams();
  const draftKeyValue = useMemo(
    () =>
      draftKey ??
      `${resource?.name}-form-${action}-draft${(initialValues as any)?.id ? `-${(initialValues as any)?.id}` : ''}`,
    [action, draftKey, initialValues, resource],
  );
  const { draftValues, clearDraft: clearStoredDraft } = useDraftData(
    form,
    latestValues,
    draftEnabled,
    draftKeyValue,
    debounceMs,
    hasInitialSetted,
    excludedDraftFields,
  );
  const clearDraft = () => {
    clearStoredDraft();
    draftTouchedRef.current = false;
    setWarnWhen(false);
  };

  // Valores iniciales del form
  const baseInitialValues = useMemo(() => {
    return omitKeys(
      flattenValues(initialValues as Record<string, any> | undefined),
      excludedDraftFields,
    );
  }, [excludedDraftFields, initialValues]);
  // Valores del draft normalizados
  const normalizedDraftValues = useMemo(() => {
    return omitKeys(
      flattenValues(draftValues as Record<string, any> | undefined),
      excludedDraftFields,
    );
  }, [draftValues, excludedDraftFields]);
  // Valores iniciales form + draft
  const mergedInitialValues = useMemo(() => {
    const base = baseInitialValues;

    // El draft tambien se mezcla normalizado para que objetos de Refine/AntD no pisen ids simples.
    return normalizedDraftValues ? { ...(base ?? {}), ...normalizedDraftValues } : base;
  }, [baseInitialValues, normalizedDraftValues]);
  // Detecta si hay cambios entre initialValues y draftValues
  const hasDraftChanges = useMemo(() => {
    if (!draftValues) return false;

    return !areShallowEqual(baseInitialValues, normalizedDraftValues);
  }, [baseInitialValues, draftValues, normalizedDraftValues]);

  // Aplica initialValues + draft una sola vez cuando el form existe.
  useEffect(() => {
    if (!form || !mergedInitialValues) return;

    const baseInitialValuesChanged = !areShallowEqual(
      lastAppliedBaseInitialValuesRef.current,
      baseInitialValues,
    );
    const shouldApplyForFirstTime = !hasInitialSetted;
    // Si backend trae nuevos initialValues (ej: luego de guardar) y no hay draft activo,
    // se reaplican al formulario para evitar desincronización UI vs estado real.
    const shouldReapplyFromBackend =
      hasInitialSetted && baseInitialValuesChanged && !hasDraftChanges;

    if (!shouldApplyForFirstTime && !shouldReapplyFromBackend) {
      return;
    }

    form.setFieldsValue(mergedInitialValues as any);
    setHasInitialSetted(true);
    lastAppliedBaseInitialValuesRef.current = baseInitialValues;
  }, [baseInitialValues, form, hasDraftChanges, hasInitialSetted, mergedInitialValues]);

  // Si hay diferencias entre borrador y server, marcamos los campos como tocados
  // y avisamos al notifier de unsaved changes.
  useEffect(() => {
    if (
      draftTouchedRef.current ||
      !form ||
      !draftEnabled ||
      !hasInitialSetted ||
      !hasDraftChanges ||
      !normalizedDraftValues
    ) {
      return;
    }

    const touchedFields = Object.keys(normalizedDraftValues).map(name => ({
      name,
      touched: true,
    }));

    form.setFields(touchedFields as any);
    setWarnWhen(true);
    draftTouchedRef.current = true;
  }, [draftEnabled, form, hasDraftChanges, hasInitialSetted, normalizedDraftValues, setWarnWhen]);

  return {
    initialValuesSetted: hasInitialSetted,
    formInitialValues: mergedInitialValues,
    hasDraftChanges,
    clearDraft,
  };
};

const useDraftData = <T extends object>(
  form: FormInstance<T> | undefined,
  latestValues: Partial<T> | undefined,
  draftEnabled: boolean,
  draftKey: string,
  debounceMs: number,
  hasInitialSetted: boolean,
  excludedDraftFields: (keyof T | string)[],
) => {
  const timerRef = useRef<number>();
  const persistBlockedRef = useRef(false);
  const lastClearedValuesRef = useRef<Record<string, any> | undefined>(undefined);
  const storage = typeof window === 'undefined' ? undefined : window.sessionStorage;
  const storedDraft = useMemo(() => {
    if (!draftEnabled || !storage) return undefined;

    const raw = storage.getItem(draftKey);

    if (!raw) return undefined;

    try {
      return JSON.parse(raw) as Partial<T>;
    } catch {
      storage.removeItem(draftKey);

      return undefined;
    }
  }, [draftEnabled, draftKey, storage]);
  const [draftValues, setDraftValues] = useState<Partial<T> | undefined>(storedDraft);

  // Persiste el formulario completo en localStorage (debounced).
  useEffect(() => {
    if (!draftEnabled || !form || !storage || !hasInitialSetted || !draftKey) return;

    const currentValuesSnapshot = omitKeys(
      flattenValues(form.getFieldsValue(true) as Record<string, any>),
      excludedDraftFields,
    );

    if (persistBlockedRef.current) {
      // Evita recrear inmediatamente el draft tras clearDraft hasta que exista un cambio real.
      if (areShallowEqual(lastClearedValuesRef.current, currentValuesSnapshot)) {
        return;
      }

      persistBlockedRef.current = false;
      lastClearedValuesRef.current = undefined;
    }

    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      const values = omitKeys(
        flattenValues(form.getFieldsValue(true) as Record<string, any>),
        excludedDraftFields,
      );

      setDraftValues(previousDraftValues => {
        // Persiste el draft normalizado para mantener selects como ids y evitar renders por referencias nuevas.
        const draft = {
          ...(flattenValues(previousDraftValues as Record<string, any> | undefined) ?? {}),
          ...(values ?? {}),
        } as Partial<T>;
        const draftWithoutExcluded = omitKeys(
          draft as Record<string, any>,
          excludedDraftFields,
        ) as Partial<T>;

        // Evita re-render loops cuando antd entrega nuevas referencias con mismo contenido.
        if (
          areShallowEqual(
            previousDraftValues as Record<string, any> | undefined,
            draftWithoutExcluded as Record<string, any> | undefined,
          )
        ) {
          return previousDraftValues;
        }

        cleanupSiblingDraftKeys(storage, draftKey);
        storage.setItem(draftKey, JSON.stringify(draftWithoutExcluded));

        return draftWithoutExcluded;
      });
    }, debounceMs);

    return () => window.clearTimeout(timerRef.current);
  }, [
    debounceMs,
    draftEnabled,
    draftKey,
    excludedDraftFields,
    form,
    hasInitialSetted,
    latestValues,
    storage,
  ]);

  const clearDraft = () => {
    if (!storage || !form) return;

    window.clearTimeout(timerRef.current);
    persistBlockedRef.current = true;
    lastClearedValuesRef.current = omitKeys(
      flattenValues(form.getFieldsValue(true) as Record<string, any>),
      excludedDraftFields,
    );

    storage.removeItem(draftKey);
    setDraftValues(undefined);
  };

  useEffect(() => {
    return () => {
      window.clearTimeout(timerRef.current);
      clearDraft();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { storedDraft, draftValues, clearDraft };
};

// Helpers
const cleanupSiblingDraftKeys = (storage: Storage, draftKey: string) => {
  const baseKey = getDraftKeyBase(draftKey);

  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);

    if (!key || key === draftKey) continue;

    if (key === baseKey || key.startsWith(`${baseKey}-`)) {
      storage.removeItem(key);
    }
  }
};

const getDraftKeyBase = (draftKey: string) => {
  const marker = '-draft';
  const markerIndex = draftKey.lastIndexOf(marker);

  if (markerIndex === -1) return draftKey;

  return draftKey.slice(0, markerIndex + marker.length);
};

const omitKeys = (values?: Record<string, any>, keysToOmit: (string | number | symbol)[] = []) => {
  if (!values) return undefined;

  const omittedKeys = new Set(keysToOmit);

  return Object.fromEntries(Object.entries(values).filter(([key]) => !omittedKeys.has(key)));
};

const flattenValues = (values?: Record<string, any>) => {
  if (!values) return undefined;

  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      Array.isArray(value)
        ? value.map(item => (item && typeof item === 'object' && 'id' in item ? item.id : item))
        : value && typeof value === 'object' && 'id' in value
          ? value.id
          : value,
    ]),
  );
};

// Campos sin valor cargado pueden llegar como `undefined`/`null` (no están en los initialValues)
// o como `[]`/`''` (un Form.Item ya registrado pero vacío, ej. listas anidadas ocultas). Son
// equivalentes a fines de detectar cambios reales del usuario.
const isEmptyValue = (value: any) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);
const isPlainObject = (value: any) =>
  value != null && typeof value === 'object' && !Array.isArray(value);
// Recursivo: arrays anidados (ej. fail_report_items) contienen objetos planos, y esos objetos
// son instancias distintas entre baseInitialValues y el draft (uno viene de un map(), el otro de
// JSON.parse), por lo que la igualdad por referencia (a === b) siempre da falso aunque el
// contenido sea idéntico. Hay que comparar campo a campo también dentro de esos objetos.
const looseEqual = (a: any, b: any): boolean => {
  if (isEmptyValue(a) && isEmptyValue(b)) return true;

  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => looseEqual(v, b[i]));
  }

  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);

    return [...keys].every(key => looseEqual(a[key], b[key]));
  }

  return a === b || a == b;
};

const isEmptyObject = (value: any) =>
  value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0;
const areShallowEqual = (a?: Record<string, any>, b?: Record<string, any>): boolean => {
  if ((!a || isEmptyObject(a)) && (!b || isEmptyObject(b))) return true;
  if (!a || !b) return false;

  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);

  for (const key of keys) {
    if (!looseEqual(a[key], b[key])) {
      return false;
    }
  }

  return true;
};

export default useFormDraftInit;
